'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { cn } from '@/lib/utils';
import { richTextComponent } from '@/sanity/sanityPortableText';
import {
  PageGeneralRulesSchema,
  GeneralRulesItem,
  GeneralRulesRow,
  GeneralRulesSubBox,
} from '@/sanity/pages/PageGeneralRulesTemplate';

const ease = [0.25, 0.1, 0.25, 1] as const;

// ── Inline SVG Icons ──────────────────────────────────────────────────────────

function CalendarIcon() {
  return (
    <svg
      viewBox="0 0 18 19.5"
      fill="none"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16.5 1.5H13.5V0H12V1.5H6V0H4.5V1.5H1.5C0.675 1.5 0 2.175 0 3V18C0 18.825 0.675 19.5 1.5 19.5H16.5C17.325 19.5 18 18.825 18 18V3C18 2.175 17.325 1.5 16.5 1.5ZM16.5 18H1.5V7.5H16.5V18ZM16.5 6H1.5V3H4.5V4.5H6V3H12V4.5H13.5V3H16.5V6Z"
        fill="white"
      />
    </svg>
  );
}

function TargetIcon() {
  return (
    <svg
      viewBox="0 0 21 21"
      fill="none"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M21 9.75H19.4619C19.2823 7.63226 18.3594 5.64631 16.8565 4.14347C15.3537 2.64063 13.3677 1.71775 11.25 1.5381V0H9.75V1.5381C7.63226 1.71775 5.64631 2.64063 4.14347 4.14347C2.64063 5.64631 1.71775 7.63226 1.5381 9.75H0V11.25H1.5381C1.71775 13.3677 2.64063 15.3537 4.14347 16.8565C5.64631 18.3594 7.63226 19.2823 9.75 19.4619V21H11.25V19.4619C13.3677 19.2823 15.3537 18.3594 16.8565 16.8565C18.3594 15.3537 19.2823 13.3677 19.4619 11.25H21V9.75ZM11.25 17.9619V15H9.75V17.9619C8.02966 17.7865 6.42249 17.023 5.19972 15.8003C3.97695 14.5775 3.21346 12.9703 3.0381 11.25H6V9.75H3.0381C3.21346 8.02966 3.97695 6.42249 5.19972 5.19972C6.42249 3.97695 8.02966 3.21346 9.75 3.0381V6H11.25V3.0381C12.9703 3.21346 14.5775 3.97695 15.8003 5.19972C17.023 6.42249 17.7865 8.02966 17.9619 9.75H15V11.25H17.9619C17.7865 12.9703 17.023 14.5775 15.8003 15.8003C14.5775 17.023 12.9703 17.7865 11.25 17.9619Z"
        fill="white"
      />
    </svg>
  );
}

function PercentageIcon() {
  return (
    <svg
      viewBox="0 0 18 18"
      fill="none"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3.75001 7.5C3.00833 7.5 2.2833 7.28007 1.66662 6.86801C1.04993 6.45596 0.569287 5.87029 0.285459 5.18506C0.00163018 4.49984 -0.0726323 3.74584 0.0720623 3.01841C0.216757 2.29098 0.57391 1.6228 1.09836 1.09835C1.6228 0.573904 2.29099 0.216751 3.01842 0.0720569C3.74585 -0.0726377 4.49985 0.00162482 5.18507 0.285453C5.87029 0.569282 6.45596 1.04993 6.86802 1.66661C7.28007 2.2833 7.50001 3.00832 7.50001 3.75C7.49891 4.74423 7.10348 5.69742 6.40045 6.40045C5.69743 7.10347 4.74423 7.49891 3.75001 7.5ZM3.75001 1.5C3.305 1.5 2.86998 1.63196 2.49997 1.8792C2.12996 2.12643 1.84158 2.47783 1.67128 2.88896C1.50098 3.3001 1.45642 3.7525 1.54324 4.18895C1.63006 4.62541 1.84435 5.02632 2.15902 5.34099C2.47368 5.65566 2.8746 5.86995 3.31105 5.95677C3.74751 6.04359 4.19991 5.99903 4.61104 5.82873C5.02218 5.65843 5.37358 5.37005 5.62081 5.00003C5.86805 4.63002 6.00001 4.19501 6.00001 3.75C5.99935 3.15347 5.76209 2.58155 5.34027 2.15973C4.91846 1.73792 4.34654 1.50066 3.75001 1.5Z"
        fill="white"
      />
      <path
        d="M16.9394 3.58227e-05L0 16.9395L1.06055 18L18 1.06059L16.9394 3.58227e-05Z"
        fill="white"
      />
      <path
        d="M14.25 18C13.5083 18 12.7833 17.7801 12.1666 17.368C11.5499 16.956 11.0693 16.3703 10.7855 15.6851C10.5016 14.9998 10.4274 14.2458 10.5721 13.5184C10.7168 12.791 11.0739 12.1228 11.5984 11.5984C12.1228 11.0739 12.791 10.7168 13.5184 10.5721C14.2458 10.4274 14.9998 10.5016 15.6851 10.7855C16.3703 11.0693 16.956 11.5499 17.368 12.1666C17.7801 12.7833 18 13.5083 18 14.25C17.9989 15.2442 17.6035 16.1974 16.9005 16.9004C16.1974 17.6035 15.2442 17.9989 14.25 18ZM14.25 12C13.805 12 13.37 12.132 13 12.3792C12.63 12.6264 12.3416 12.9778 12.1713 13.389C12.001 13.8001 11.9564 14.2525 12.0432 14.689C12.1301 15.1254 12.3443 15.5263 12.659 15.841C12.9737 16.1557 13.3746 16.37 13.8111 16.4568C14.2475 16.5436 14.6999 16.499 15.111 16.3287C15.5222 16.1584 15.8736 15.87 16.1208 15.5C16.368 15.13 16.5 14.695 16.5 14.25C16.4994 13.6535 16.2621 13.0815 15.8403 12.6597C15.4185 12.2379 14.8465 12.0007 14.25 12Z"
        fill="white"
      />
    </svg>
  );
}

function HedgingIcon() {
  return (
    <svg
      viewBox="0 0 22.5 22.5"
      fill="none"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.1584 8.34134C14.1163 8.29942 14.0631 8.27032 14.0051 8.25743C13.9471 8.24454 13.8866 8.24838 13.8307 8.26851L6.95571 10.7686C6.8962 10.7903 6.84466 10.8295 6.80793 10.881C6.77119 10.9326 6.75099 10.9941 6.75 11.0575C6.74901 11.1208 6.76727 11.1829 6.80237 11.2356C6.83747 11.2883 6.88776 11.3291 6.94656 11.3526L9.94701 12.5526L11.147 15.5531C11.1702 15.6112 11.2102 15.6611 11.262 15.6961C11.3138 15.7312 11.3749 15.75 11.4375 15.75H11.4425C11.5058 15.7489 11.5673 15.7287 11.6188 15.692C11.6704 15.6553 11.7095 15.6037 11.7312 15.5443L14.2312 8.66908C14.2515 8.6132 14.2554 8.55266 14.2426 8.49461C14.2297 8.43655 14.2005 8.38337 14.1584 8.34134Z"
        fill="white"
      />
      <path
        d="M22.0606 12.3106C22.1999 12.1713 22.3104 12.006 22.3858 11.824C22.4612 11.642 22.5 11.447 22.5 11.25C22.5 11.053 22.4612 10.858 22.3858 10.676C22.3104 10.494 22.1999 10.3287 22.0606 10.1894L17.7204 5.84946L19.8958 3.70236C20.2167 3.78186 20.5548 3.75327 20.8577 3.621C21.1606 3.48874 21.4114 3.2602 21.5712 2.97084C21.731 2.68149 21.7908 2.34749 21.7414 2.02066C21.692 1.69383 21.5361 1.39245 21.298 1.16326C21.0598 0.934064 20.7526 0.789881 20.4241 0.753073C20.0956 0.716265 19.7642 0.788891 19.4812 0.959682C19.1982 1.13047 18.9795 1.38989 18.859 1.69767C18.7384 2.00546 18.7229 2.34442 18.8146 2.66196L16.6597 4.78858L12.3106 0.43941C12.1713 0.300103 12.006 0.189596 11.824 0.114201C11.642 0.0388058 11.447 0 11.25 0C11.053 0 10.858 0.0388058 10.676 0.114201C10.494 0.189596 10.3287 0.300103 10.1894 0.43941L5.84946 4.77944L3.70258 2.60376C3.78204 2.28287 3.75338 1.94472 3.62104 1.64178C3.4887 1.33884 3.26008 1.08804 2.97066 0.9283C2.68123 0.768555 2.34717 0.708793 2.02032 0.758284C1.69346 0.807775 1.39207 0.963754 1.16291 1.20202C0.933744 1.44029 0.789621 1.74752 0.752896 2.07606C0.716171 2.4046 0.788898 2.73607 0.959793 3.01906C1.13069 3.30204 1.3902 3.52072 1.69806 3.64116C2.00592 3.76161 2.34493 3.77708 2.66249 3.68518L4.78874 5.84031L0.43941 10.1894C0.300103 10.3287 0.189596 10.494 0.114201 10.676C0.0388058 10.858 0 11.053 0 11.25C0 11.447 0.0388058 11.642 0.114201 11.824C0.189596 12.006 0.300103 12.1713 0.43941 12.3106L4.78858 16.6597L2.66249 18.8148C2.34565 18.7236 2.00756 18.7394 1.70064 18.8598C1.39371 18.9803 1.13509 19.1986 0.964847 19.481C0.794606 19.7633 0.722256 20.0939 0.759013 20.4216C0.795769 20.7493 0.939578 21.0556 1.16815 21.2933C1.39672 21.5309 1.69729 21.6865 2.02328 21.7359C2.34926 21.7853 2.68245 21.7259 2.9712 21.5667C3.25995 21.4076 3.48814 21.1576 3.6204 20.8556C3.75265 20.5536 3.7816 20.2163 3.70273 19.8962L5.84961 17.7205L10.1896 22.0606C10.3288 22.1999 10.4942 22.3104 10.6761 22.3858C10.8581 22.4612 11.0532 22.5 11.2501 22.5C11.4471 22.5 11.6422 22.4612 11.8241 22.3858C12.0061 22.3104 12.1714 22.1999 12.3107 22.0606L16.6598 17.7114L18.8148 19.838C18.7236 20.1549 18.7394 20.4931 18.8599 20.8C18.9804 21.107 19.1988 21.3657 19.4812 21.5359C19.7637 21.7061 20.0944 21.7785 20.4221 21.7416C20.7498 21.7048 21.0562 21.5609 21.2938 21.3323C21.5315 21.1036 21.687 20.803 21.7364 20.4769C21.7858 20.1509 21.7263 19.8176 21.567 19.5289C21.4077 19.2401 21.1577 19.0119 20.8556 18.8797C20.5535 18.7475 20.2162 18.7186 19.896 18.7976L17.7205 16.6505L22.0606 12.3106ZM11.25 21L1.49998 11.25L11.25 1.49998L21 11.25L11.25 21Z"
        fill="white"
      />
    </svg>
  );
}

// ── Badge ─────────────────────────────────────────────────────────────────────

function RuleBadge({
  iconType,
  number,
}: {
  iconType?: string;
  number?: string;
}) {
  if (iconType) {
    return (
      <div className="shrink-0 flex items-center justify-center w-[5rem] h-[5rem] rounded-[0.25rem] bg-black">
        <div className="w-[3rem] h-[3rem]">
          {iconType === 'calendar' && <CalendarIcon />}
          {iconType === 'target' && <TargetIcon />}
          {iconType === 'percentage' && <PercentageIcon />}
          {iconType === 'hedging' && <HedgingIcon />}
        </div>
      </div>
    );
  }
  if (number) {
    return (
      <div className="shrink-0 flex items-center justify-center w-[5rem] h-[5rem] rounded-[0.25rem] bg-black/10 text-black/60">
        <span className="font-geist-mono font-medium text-[2rem] leading-none">
          {number}
        </span>
      </div>
    );
  }
  return null;
}

// ── Sub-boxes ─────────────────────────────────────────────────────────────────

function SubBoxes({ boxes }: { boxes: GeneralRulesSubBox[] }) {
  return (
    <div className="flex flex-col md:flex-row gap-[1.5rem] mt-[1.5rem]">
      {boxes.map((box) => (
        <div
          key={box._key}
          className="flex-1 rounded-[0.25rem] p-[2.5rem] flex flex-col gap-[0.75rem] bg-black/5"
        >
          {box.title && (
            <p className="mb-0 font-geist font-bold text-[1.75rem] leading-[1.5] text-black/80">
              {box.title}
            </p>
          )}
          {box.content && (
            <div className="flex flex-col gap-[2rem] [&_p]:mb-0 [&_p]:text-[1.75rem] [&_p]:leading-[1.5] [&_p]:text-black/60">
              <PortableText
                value={box.content}
                components={richTextComponent}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Single row ────────────────────────────────────────────────────────────────

function RuleRow({
  row,
  showDivider,
}: {
  row: GeneralRulesRow;
  showDivider: boolean;
}) {
  return (
    <div className="flex flex-col gap-[2rem]">
      {row.label ? (
        <div className="flex flex-col lg:flex-row gap-[0.5rem] lg:gap-[1rem] items-start">
          <p className="mb-0 shrink-0 whitespace-nowrap font-geist font-medium text-[2rem] leading-[1.5] uppercase opacity-80 text-[#1c1f22]">
            {row.label}
          </p>
          <div className="flex-1 min-w-0 flex flex-col">
            {row.content && (
              <div className="flex flex-col gap-[2rem] [&_p]:mb-0 [&_p]:text-[2rem] [&_p]:leading-[1.5] [&_strong]:font-semibold text-[#1c1f22]/60">
                <PortableText
                  value={row.content}
                  components={richTextComponent}
                />
              </div>
            )}
            {!!row.subBoxes?.length && <SubBoxes boxes={row.subBoxes} />}
          </div>
        </div>
      ) : (
        <div className="w-full text-[#1c1f22]/60">
          {row.content && (
            <div className="flex flex-col gap-[2rem] [&_p]:mb-0 [&_p]:text-[2rem] [&_p]:leading-[1.5] [&_strong]:font-semibold [&_strong]:opacity-100">
              <PortableText
                value={row.content}
                components={richTextComponent}
              />
            </div>
          )}
          {!!row.subBoxes?.length && <SubBoxes boxes={row.subBoxes} />}
        </div>
      )}

      {showDivider && <div className="w-full h-px bg-black/10" />}
    </div>
  );
}

// ── Rule card ─────────────────────────────────────────────────────────────────

function RuleCard({
  item,
  showDividers,
}: {
  item: GeneralRulesItem;
  showDividers: boolean;
}) {
  const rows = item.rows ?? [];

  return (
    <div className="flex flex-col gap-[1.5rem] p-[3rem] rounded-[0.25rem] border border-black/10 ">
      <div className="flex gap-[2.25rem] items-center">
        <RuleBadge iconType={item.iconType} number={item.number} />
        {item.title && (
          <p className="mb-0 font-geist font-medium text-[2.5rem] leading-[1.5] text-[#1c1f22]">
            {item.title}
          </p>
        )}
      </div>

      {rows.length > 0 && (
        <div className="flex flex-col gap-[0.75rem] pl-0 lg:pl-[7rem]">
          {rows.map((row, idx) => (
            <RuleRow
              key={row._key ?? idx}
              row={row}
              showDivider={showDividers && idx < rows.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────

export default function RulesSection({
  data,
}: {
  data: PageGeneralRulesSchema;
}) {
  const [mainTab, setMainTab] = useState(0); // 0=Crypto, 1=Futures
  const [subTab, setSubTab] = useState(0);

  useEffect(() => {
    setSubTab(0);
  }, [mainTab]);

  const cryptoSubTabs = [
    {
      label: data.cryptoTab1Label ?? 'Key Rules',
      items: data.cryptoTab1Items ?? [],
    },
    {
      label: data.cryptoTab2Label ?? 'Trading Limits',
      items: data.cryptoTab2Items ?? [],
    },
    {
      label: data.cryptoTab3Label ?? 'Payout Structure & Profit Split',
      items: data.cryptoTab3Items ?? [],
    },
    {
      label: data.cryptoTab4Label ?? 'Breach Logic & Consequences',
      items: data.cryptoTab4Items ?? [],
    },
  ];

  const futuresSubTabs = [
    {
      label: data.futuresTab1Label ?? 'Key Rules',
      items: data.futuresTab1Items ?? [],
    },
    {
      label: data.futuresTab2Label ?? 'Trading Limits',
      items: data.futuresTab2Items ?? [],
    },
    {
      label: data.futuresTab3Label ?? 'Prizes, Payout Structure & Profit Split',
      items: data.futuresTab3Items ?? [],
    },
    {
      label: data.futuresTab4Label ?? 'Breach Logic & Consequences',
      items: data.futuresTab4Items ?? [],
    },
  ];

  const activeSubTabs = mainTab === 0 ? cryptoSubTabs : futuresSubTabs;
  const currentItems = activeSubTabs[subTab]?.items ?? [];
  const showDividers = subTab >= 2;

  return (
    <section className="relative w-full pb-[15rem] lg:pb-[20rem]">
      <div className="mx-auto w-[min(170.5rem,calc(100%-4rem))] lg:w-[min(170.5rem,calc(100%-8rem))]">
        {/* ── Outer tabs: Futures | Crypto ─────────────────────────────────── */}
        <div className="flex items-end justify-center gap-[2rem] lg:gap-[4rem]">
          {[
            { label: data.cryptoTabLabel ?? 'Crypto', idx: 0 },
            { label: data.futuresTabLabel ?? 'Futures', idx: 1 },
          ].map(({ label, idx }) => (
            <button
              key={idx}
              onClick={() => setMainTab(idx)}
              className={cn(
                'flex-1 lg:flex-none lg:w-[35rem] py-[2rem] text-[2.5rem] font-medium lg:py-[3rem] font-geist-mono uppercase tracking-[-0.1rem] transition-colors flex items-center justify-center rounded-t-[0.25rem]',
                mainTab === idx
                  ? 'bg-[#F4F4F4] text-black  border border-black/10 border-b-0 relative z-10 mb-[-1px]'
                  : 'bg-[#e8e8e8] text-black/[0.33] ',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Content panel ────────────────────────────────────────────────── */}
        <div className="bg-[#F4F4F4] border border-black/10 px-[3rem] pb-[3rem] pt-[3rem] lg:px-[5rem] lg:pb-[5rem] lg:pt-[4rem] flex flex-col gap-[3rem]">
          {/* ── Sub-tabs ─────────────────────────────────────────────────── */}
          <div className="flex gap-[1.5rem] overflow-x-auto scrollbar-hide pb-[0.5rem] -mb-[0.5rem]">
            {activeSubTabs.map((tab, i) => (
              <button
                key={i}
                onClick={() => setSubTab(i)}
                className={cn(
                  'shrink-0 px-[2.5rem] py-[1.5rem] rounded-[0.25rem] font-geist font-medium text-[2rem] leading-none tracking-[-0.05rem] transition-colors border',
                  subTab === i
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-black border-black/10',
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Rule cards ───────────────────────────────────────────────── */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`${mainTab}-${subTab}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.3, ease }}
              className="flex flex-col gap-[1.5rem]"
            >
              {currentItems.map((item, idx) => (
                <motion.div
                  key={item._key ?? idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, ease, delay: idx * 0.05 }}
                >
                  <RuleCard item={item} showDividers={showDividers} />
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
